import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Icon,
  SidebarMenuButton,
  SidebarMenuItem,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import React, { useState } from "react";

import { useAppData } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import { useWorkflowOrder } from "#app/lib/hooks/use-workflow-order";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { CreateWorkflowDialog } from "#app/components/workflows/create-workflow-dialog";
import { WorkflowShareDialog } from "#app/components/workflows/workflow-share-dialog";
import { SidebarSection } from "@salestrak/ui";
import { WorkflowItem } from "../workflow-item";
import { useSidebarNav } from "../use-sidebar-nav";

/**
 * "My Workflows" module-menu section — the user's ordered workflow list
 * (drag-sortable on desktop) plus a "Create workflow" row (gated on the
 * `create.workflows` permission). Shared by the Home and Workflows module
 * menus.
 */
export function MyWorkflowsSection() {
  const { navigate, isActive } = useSidebarNav();
  const { workflows, refetch: refetchAppData } = useAppData();
  const { sortedWorkflows, workflowIds, handleDragEnd } =
    useWorkflowOrder(workflows);
  const canCreateWorkflow = usePermission("create.workflows");
  // "Edit workflow" opens the workflow settings page, itself guarded by
  // manage.edit_workflows — hide the menu item without that permission.
  const canEditWorkflows = usePermission("manage.edit_workflows");
  const isMobile = useIsMobile();
  const closeSidebar = useCloseSidebarIfMobile();
  const [createWorkflowOpen, setCreateWorkflowOpen] = useState(false);
  const [shareWorkflowId, setShareWorkflowId] = useState<string | null>(null);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const sensors = useSensors(isMobile ? null : pointerSensor);

  const openCreateWorkflow = () => {
    closeSidebar();
    setCreateWorkflowOpen(true);
  };

  // With no visible workflows AND no create permission there's nothing to
  // show or do — hide the whole section rather than render an empty,
  // actionless accordion. (No workflows + can create → keep it for the CTA.)
  if (sortedWorkflows.length === 0 && !canCreateWorkflow) return null;

  return (
    <>
      <SidebarSection
        // Header "+" — same flow as the Create workflow row below; omitted
        // entirely without create permission.
        action={
          canCreateWorkflow
            ? {
                icon: "plus",
                label: "Create workflow",
                onClick: openCreateWorkflow,
              }
            : undefined
        }
        title="My Workflows"
      >
        <DndContext
          collisionDetection={closestCenter}
          key={isMobile ? "mobile" : "desktop"}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={workflowIds}
            strategy={verticalListSortingStrategy}
          >
            {sortedWorkflows.map((wf, index) => (
              <WorkflowItem
                isActive={isActive(`/workflow/${wf.workflowId}`)}
                key={`${wf.workflowId}-${index}`}
                onClick={() => navigate(`/workflow/${wf.workflowId}`)}
                onEdit={
                  canEditWorkflows
                    ? () => navigate(`/settings/workflows/${wf.workflowId}`)
                    : undefined
                }
                onShare={
                  // Backend-resolved share-management gate — the exact rule
                  // the sharing mutations enforce (manage.users OR explicit
                  // full_edit / creator / workflow-editor). Never derive this
                  // from viewerAccessLevel: the public defaultAccessLevel
                  // raises that level for everyone without granting share
                  // management.
                  wf.viewerCanManageSharing
                    ? () => {
                        closeSidebar();
                        setShareWorkflowId(wf.workflowId);
                      }
                    : undefined
                }
                sortable={!isMobile}
                workflow={wf}
              />
            ))}
          </SortableContext>
        </DndContext>
        {canCreateWorkflow && (
          <SidebarMenuItem>
            <SidebarMenuButton onClick={openCreateWorkflow}>
              <Icon name="plus" />
              <Text truncate>Create workflow</Text>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
      </SidebarSection>
      {canCreateWorkflow && (
        <CreateWorkflowDialog
          onCreated={async (workflowId) => {
            await refetchAppData();
            navigate(
              canEditWorkflows
                ? `/settings/workflows/${workflowId}`
                : `/workflow/${workflowId}`,
            );
          }}
          onOpenChange={setCreateWorkflowOpen}
          open={createWorkflowOpen}
        />
      )}
      <WorkflowShareDialog
        onOpenChange={(open) => {
          if (!open) setShareWorkflowId(null);
        }}
        workflowId={shareWorkflowId}
      />
    </>
  );
}
